import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useSite } from "../lib/site";
import ArticleCard from "../components/ArticleCard";
import Reveal from "../components/Reveal";
import Skeleton from "../components/Skeleton";

export default function Articles() {
  const { settings } = useSite();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const kategori = params.get("kategori") || "";
  const tag = params.get("tag") || "";
  const hal = parseInt(params.get("hal") || "1", 10);

  const [search, setSearch] = useState(q);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [contentPage, setContentPage] = useState({});

  useSeo({ title: "Tulisan — Heri Sensei", description: "Kumpulan tulisan Heri Sensei tentang pendidikan, karakter, Jepang, bisnis, dan kehidupan.", path: "/tulisan" });

  useEffect(() => {
    api.get("/content/articles").then((r) => setContentPage(r.data || {})).catch(() => setContentPage({}));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== q) {
        const next = new URLSearchParams(params);
        if (search) next.set("q", search); else next.delete("q");
        next.delete("hal");
        setParams(next, { replace: true });
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("search", q);
    if (kategori) qs.set("category", kategori);
    if (tag) qs.set("tag", tag);
    qs.set("page", String(hal));
    api.get(`/articles?${qs.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, pages: 1 }))
      .finally(() => setLoading(false));
  }, [q, kategori, tag, hal]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete("hal");
    setParams(next);
  };

  const categories = settings.categories || [];
  const pageCopy = contentPage || {};
  const goPage = (p) => {
    const next = new URLSearchParams(params);
    if (p > 1) next.set("hal", String(p)); else next.delete("hal");
    setParams(next);
  };

  return (
    <div data-testid="articles-page" className="max-w-6xl mx-auto px-4 sm:px-8 py-14 md:py-20">
      <p className="text-xs uppercase tracking-[0.22em] font-semibold text-enji">{pageCopy.eyebrow || "Tulisan"}</p>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-4 text-ink">{pageCopy.title || "Catatan & pandangan"}</h1>
      <p className="text-sub mt-4 max-w-xl">{pageCopy.intro || "Pencarian nyata di seluruh isi tulisan. Gunakan filter kategori atau tag untuk menjelajah."}</p>

      <div className="mt-10 flex flex-col gap-5">
        <div className="relative max-w-md">
          <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-faint" />
          <input
            data-testid="article-search-input"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari judul, ringkasan, atau isi tulisan…"
            className="field-input !pl-11"
            aria-label="Cari tulisan"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" role="group" aria-label="Filter kategori">
          <button
            data-testid="category-chip-semua"
            onClick={() => setParam("kategori", "")}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${!kategori ? "bg-enji text-white border-enji" : "border-line bg-surface text-sub hover:text-ink"}`}
          >
            Semua
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`category-chip-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setParam("kategori", kategori === cat ? "" : cat)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${kategori === cat ? "bg-enji text-white border-enji" : "border-line bg-surface text-sub hover:text-ink"}`}
            >
              {cat}
            </button>
          ))}
        </div>
        {tag && (
          <p className="text-sm text-sub">
            Tag: <span className="inline-flex items-center gap-2 bg-surface border border-line rounded-full px-3 py-1">{tag}
              <button onClick={() => setParam("tag", "")} aria-label="Hapus filter tag" className="text-faint hover:text-enji">×</button>
            </span>
          </p>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12" aria-busy="true">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-80" />
          ))}
        </div>
      ) : data.items.length === 0 ? (
        <div data-testid="articles-empty" className="mt-16 text-center border border-dashed border-line rounded-xl py-20">
          <span className="w-2.5 h-2.5 rounded-full bg-enji inline-block mb-5" />
          <p className="font-serif text-2xl text-ink">{pageCopy.empty_title || "Belum ada tulisan yang cocok"}</p>
          <p className="text-sub mt-2">{pageCopy.empty_text || "Coba kata kunci lain atau lihat semua tulisan."}</p>
          <button onClick={() => setParams(new URLSearchParams())} className="btn-ghost mt-8">{pageCopy.empty_action_label || "Tampilkan semua"}</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {data.items.map((a, i) => (
              <ArticleCard key={a.id} article={a} index={i} />
            ))}
          </div>
          {data.pages > 1 && (
            <nav className="flex items-center justify-center gap-2 mt-14" aria-label="Navigasi halaman">
              {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  data-testid={`pagination-page-${p}`}
                  onClick={() => goPage(p)}
                  className={`w-11 h-11 rounded-full text-sm font-semibold border transition-colors ${p === hal ? "bg-enji text-white border-enji" : "border-line bg-surface text-sub hover:text-ink"}`}
                >
                  {p}
                </button>
              ))}
            </nav>
          )}
        </>
      )}
      <Reveal className="mt-20 text-center">
        <Link to="/newsletter" className="link-underline text-sub hover:text-ink text-sm">
          {pageCopy.newsletter_cta || "Ingin tulisan baru sampai ke email Anda? Berlangganan newsletter"}
        </Link>
      </Reveal>
    </div>
  );
}
